import type { LayerHealth, LayerRegistryEntry } from '@okmaps/shared';
import { baseApi } from './baseApi.js';

/**
 * The layer registry: what can actually be drawn, derived from the upstream
 * WMS and WFS capabilities documents rather than from the catalog.
 *
 * Fetched once per session and cached indefinitely. It is a few hundred
 * kilobytes of JSON and it is the join table behind every "can I add this to
 * the map?" decision in the app.
 */
export const layerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLayerRegistry: build.query<readonly LayerRegistryEntry[], void>({
      query: () => 'layers',
      providesTags: ['Layer'],
      // The registry only changes when OGI publishes a layer. Never refetch
      // within a session.
      keepUnusedDataFor: 3600,
    }),

    /**
     * Whether upstream can draw a layer at all.
     *
     * Eight published layers cannot be drawn for reasons entirely upstream - a
     * NOAA host that no longer resolves, missing granule files, an empty layer
     * group. Nothing here can fix them, but presenting them as an unexplained
     * blank map is worse than saying so, and indistinguishable from a layer
     * that is merely outside its scale window.
     *
     * Asked once per layer per session: the proxy probes upstream, so this is
     * not free, and the answer changes on OGI's timescale rather than ours.
     */
    getLayerHealth: build.query<LayerHealth, string>({
      query: (name) => `layers/${encodeURIComponent(name)}/health`,
      providesTags: (_r, _e, name) => [{ type: 'Metadata' as const, id: name }],
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const { useGetLayerRegistryQuery, useGetLayerHealthQuery } = layerApi;

/** Legend graphics are images, so they are built as URLs rather than fetched. */
export function legendUrl(layerName: string): string {
  const base = import.meta.env['VITE_API_BASE'] ?? '/api';
  return `${base}/legend?layer=${encodeURIComponent(layerName)}`;
}
